import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { PasswordInput } from "../../components/ui/password-input";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import { ShieldCheck, ShieldOff } from "lucide-react";

export default function AdminSecurity() {
  const [me, setMe] = useState(null);
  const [setup, setSetup] = useState(null);
  const [otp, setOtp] = useState("");
  const [disableForm, setDisableForm] = useState({ password: "", otp: "" });
  const [passwordForm, setPasswordForm] = useState({ current_password: "", new_password: "" });

  async function load() {
    const r = await api.get("/auth/me");
    setMe(r.data);
  }
  useEffect(() => { load(); }, []);

  async function startSetup() {
    const r = await api.post("/auth/mfa/setup");
    setSetup(r.data);
  }

  async function enableMfa() {
    try {
      await api.post("/auth/mfa/enable", { otp });
      toast.success("MFA ativado");
      setSetup(null);
      setOtp("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Código inválido");
    }
  }

  async function disableMfa() {
    try {
      await api.post("/auth/mfa/disable", disableForm);
      toast.success("MFA desativado");
      setDisableForm({ password: "", otp: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro");
    }
  }

  async function changePassword() {
    if (passwordForm.new_password.length < 8) {
      toast.error("A nova senha deve ter no mínimo 8 caracteres");
      return;
    }
    try {
      await api.post("/auth/change-password", passwordForm);
      toast.success("Senha atualizada");
      setPasswordForm({ current_password: "", new_password: "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro");
    }
  }

  if (!me) return null;

  return (
    <div className="max-w-2xl" data-testid="admin-security-page">
      <div className="mb-8">
        <span className="text-[11px] uppercase tracking-[0.28em] text-slate-500">Sua conta</span>
        <h1 className="text-3xl font-semibold text-[#0AA89C] mt-1">Segurança</h1>
      </div>

      <section className="bg-white border border-slate-200 rounded-md p-6 mb-6">
        <div className="flex items-center gap-3 mb-4">
          <ShieldCheck className="w-5 h-5 text-[#0AA89C]" />
          <h2 className="font-semibold">Autenticação em dois fatores (MFA)</h2>
        </div>
        {me.mfa_enabled ? (
          <div>
            <p className="text-sm text-emerald-700 mb-4">MFA ativado — sua conta exige código autenticador a cada login.</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Senha atual</Label>
                <PasswordInput value={disableForm.password} onChange={(e) => setDisableForm({ ...disableForm, password: e.target.value })} autoComplete="current-password" />
              </div>
              <div>
                <Label>Código MFA</Label>
                <Input inputMode="numeric" maxLength={6} value={disableForm.otp} onChange={(e) => setDisableForm({ ...disableForm, otp: e.target.value.replace(/\D/g, "") })} />
              </div>
            </div>
            <Button variant="outline" className="mt-4" onClick={disableMfa} data-testid="disable-mfa">
              <ShieldOff className="w-4 h-4 mr-1" /> Desativar MFA
            </Button>
          </div>
        ) : setup ? (
          <div>
            <p className="text-sm text-slate-600 mb-3">
              1. Escaneie o QR code abaixo no seu app autenticador (Google Authenticator, 1Password, Authy...)<br />
              2. Digite o código gerado para confirmar.
            </p>
            <div className="border border-slate-200 rounded p-3 inline-block bg-white" dangerouslySetInnerHTML={{ __html: setup.qr_svg }} />
            <div className="text-xs text-slate-500 mt-2">Ou digite manualmente: <code className="font-mono">{setup.secret}</code></div>
            <div className="mt-4 flex gap-2 items-end">
              <div className="flex-1">
                <Label>Código do app</Label>
                <Input inputMode="numeric" maxLength={6} value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))} data-testid="mfa-code" />
              </div>
              <Button className="bg-[#0AA89C] hover:bg-[#066B63]" onClick={enableMfa} data-testid="enable-mfa">Ativar</Button>
            </div>
          </div>
        ) : (
          <div>
            <p className="text-sm text-slate-600 mb-3">
              Recomendado especialmente para perfis Diretor e Financeiro. Requer app autenticador (TOTP).
            </p>
            <Button className="bg-[#0AA89C] hover:bg-[#066B63]" onClick={startSetup} data-testid="start-mfa">
              Ativar MFA
            </Button>
          </div>
        )}
      </section>

      <section className="bg-white border border-slate-200 rounded-md p-6">
        <h2 className="font-semibold mb-4">Alterar senha</h2>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Senha atual</Label>
            <PasswordInput value={passwordForm.current_password} onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })} data-testid="current-password" autoComplete="current-password" />
          </div>
          <div>
            <Label>Nova senha</Label>
            <PasswordInput value={passwordForm.new_password} onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })} data-testid="new-password" autoComplete="new-password" />
          </div>
        </div>
        <Button className="mt-4 bg-[#0AA89C] hover:bg-[#066B63]" onClick={changePassword} data-testid="save-password">Salvar</Button>
      </section>
    </div>
  );
}
